import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import {
  FaShoppingCart, FaArrowRight, FaSpinner, FaCheckCircle,
  FaTimes, FaWallet, FaBolt, FaTint, FaLeaf, FaStar,
  FaShippingFast, FaShieldAlt, FaInfinity
} from 'react-icons/fa';
import '../styles/Home.css';
import reanimeitorImage from '../Assets/reanimeitor-can.png.png';
import packImage from '../Assets/pack24.png';
import ShippingForm from '../components/ShippingForm';

const Home = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [purchasedPack, setPurchasedPack] = useState(null);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [soldCans, setSoldCans] = useState(2855);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSoldCans(prev => prev + Math.floor(Math.random() * 3));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get('https://reanimeitor.com/backend/profile.php', { withCredentials: true });
      if (res.data.success && res.data.user) {
        setUser(res.data.user);
        localStorage.setItem('userData', JSON.stringify(res.data.user));
      } else {
        setUser(null);
        localStorage.removeItem('userData');
      }
    } catch (err) {
      setUser(null);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('pack_success');
    const sessionId = params.get('session_id');
    if (success === 'true' && sessionId) {
      const verify = async () => {
        try {
          const res = await axios.post(
            'https://reanimeitor.com/backend/verify_payment.php',
            { session_id: sessionId },
            { withCredentials: true }
          );
          if (res.data.success && res.data.paid) {
            setPurchasedPack({ name: res.data.pack_name });
            setShowSuccessPopup(true);
            await loadUser();
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error(err);
        }
      };
      verify();
    }
  }, []);

  const packsData = [
    {
      id: 2,
      name: 'Pack 24',
      price: 35.76,
      pricePerCan: 1.49,
      description: '24 latas de energía real. El pack ideal para empezar.',
      image: packImage,
      features: ['⚡ 51 kcal/100ml', '☕ Cafeína 30mg/100ml', '🍬 Azúcares 11.5g/100ml'],
      color: '#60a5fa',
      tag: 'MÁS VENDIDO'
    },
    {
      id: 3,
      name: 'Pack 48',
      price: 66.72,
      pricePerCan: 1.39,
      description: '48 latas. Mejor precio por lata para los que no paran.',
      image: packImage,
      features: ['⚡ 51 kcal/100ml', '☕ Cafeína 30mg/100ml', '🍬 Azúcares 11.5g/100ml', '🏆 Mejor precio'],
      color: '#a855f7',
      tag: 'AHORRO MÁXIMO',
      bestValue: true
    }
  ];

  const handleBuy = (pack) => {
    if (!user) {
      localStorage.setItem('pending_pack', JSON.stringify(pack));
      navigate('/login');
      return;
    }
    setSelectedPack(pack);
    setShowShippingForm(true);
  };

  const handleShippingSuccess = async (shippingData) => {
    setShowShippingForm(false);
    setIsProcessing(true);
    try {
      await axios.post('https://reanimeitor.com/backend/save_shipping_address.php', {
        user_id: user.id,
        full_name: shippingData.full_name,
        phone: shippingData.phone,
        address: shippingData.address,
        city: shippingData.city,
        postal_code: shippingData.postal_code,
        country: shippingData.country || 'España'
      }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });

      const res = await axios.post('https://reanimeitor.com/backend/create_stripe_session_pack.php',
        { pack_id: selectedPack.id },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data.success && res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        alert('Error al crear sesión de pago');
      }
    } catch (err) {
      console.error(err);
      alert('Error de conexión');
    } finally {
      setIsProcessing(false);
    }
  };

  const scrollToPacks = () => {
    document.querySelector('.packs-futuristic')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="home-futuristic">
      <div className="energy-background">
        <div className="particle-field"></div>
        <div className="grid-overlay"></div>
      </div>

      {showShippingForm && selectedPack && (
        <ShippingForm pack={selectedPack} user={user} onSuccess={handleShippingSuccess} onCancel={() => setShowShippingForm(false)} />
      )}
      {showSuccessPopup && purchasedPack && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <button className="popup-close-btn" onClick={() => setShowSuccessPopup(false)}><FaTimes /></button>
            <div className="popup-icon"><FaCheckCircle /></div>
            <h3>¡PEDIDO CONFIRMADO!</h3>
            <p className="popup-pack-name"><strong>{purchasedPack.name}</strong></p>
            <p>Prepárate para la energía. Llega en 24-48h.</p>
            <div className="popup-actions">
              <button className="popup-btn primary" onClick={() => { setShowSuccessPopup(false); navigate('/account?tab=orders'); }}>Ver pedido</button>
              <button className="popup-btn secondary" onClick={() => setShowSuccessPopup(false)}>Seguir comprando</button>
            </div>
          </div>
        </div>
      )}

      {/* HERO FUTURISTA */}
      <section className="hero-futuristic">
        <div className="hero-container">
          <div className="hero-main">
            <div className="hero-left">
              <h1 className="futuristic-title">
                <span className="title-glitch" data-text="REANIMEITOR">REANIMEITOR</span>
                <span className="title-sub">ENERGÍA QUE TE TRASCIENDE</span>
              </h1>
              <div className="hero-stats">
                <div className="stat-item">
                  <FaInfinity className="stat-icon" />
                  <span>{soldCans.toLocaleString()}</span>
                  <small>latas vendidas</small>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <FaStar className="stat-icon" />
                  <span>4.98</span>
                  <small>★★★★★</small>
                </div>
              </div>
              <p className="hero-description-future">
                51 kcal/100ml · Cafeína 30mg/100ml · Azúcares 11.5g/100ml
                <br />La fórmula perfecta para cuando necesitas reactivarte.
              </p>
              <button className="hero-cta-future" onClick={scrollToPacks}>
                ACTIVAR ENERGÍA <FaArrowRight className="cta-icon-future" />
              </button>
            </div>
            <div className="hero-right">
              <div className="can-3d-container">
                <div className="can-glow-ring"></div>
                <div className="can-rotator">
                  <img src={reanimeitorImage} alt="reanimeitor" className="can-3d" />
                </div>
                <div className="energy-particles">
                  <span></span><span></span><span></span><span></span><span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={scrollToPacks}>
          <span></span><span></span><span></span>
        </div>
      </section>

      {/* PACKS */}
      <section className="packs-futuristic">
        <div className="section-header-future">
          <h2>ELIGE TU <span className="highlight">CARGA</span></h2>
          <div className="header-beam"></div>
        </div>
        {isMobile ? (
          <Swiper modules={[Pagination, EffectCoverflow]} effect="coverflow" grabCursor centeredSlides slidesPerView="auto" coverflowEffect={{ rotate: 30, stretch: 0, depth: 100, modifier: 1, slideShadows: true }} pagination={{ clickable: true }} className="packs-swiper-future">
            {packsData.map(pack => (
              <SwiperSlide key={pack.id}>
                <FuturisticCard pack={pack} user={user} onBuy={handleBuy} isProcessing={isProcessing} />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="packs-grid-future">
            {packsData.map(pack => (
              <FuturisticCard key={pack.id} pack={pack} user={user} onBuy={handleBuy} isProcessing={isProcessing} />
            ))}
          </div>
        )}
      </section>

      {/* CARACTERÍSTICAS REALES */}
      <section className="features-futuristic">
        <div className="features-grid">
          <div className="feature-future"><FaBolt /><h4>ENERGÍA CONTROLADA</h4><p>51 kcal por 100ml</p></div>
          <div className="feature-future"><FaTint /><h4>CAFEÍNA OPTIMA</h4><p>30mg/100ml · Efecto sostenido</p></div>
          <div className="feature-future"><FaLeaf /><h4>SABOR INTENSO</h4><p>Azúcares 11.5g/100ml</p></div>
          <div className="feature-future"><FaShippingFast /><h4>ENVÍO RÁPIDO</h4><p>24-48h a tu casa</p></div>
        </div>
      </section>
    </div>
  );
};

const FuturisticCard = ({ pack, user, onBuy, isProcessing }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: x * 15, y: y * 15 });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });
  return (
    <div className="pack-card-future" ref={cardRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{ transform: `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)` }}>
      {pack.tag && <div className="pack-tag">{pack.tag}</div>}
      <div className="pack-image-future"><img src={pack.image} alt={pack.name} /></div>
      <h3>{pack.name}</h3>
      <p className="pack-desc-future">{pack.description}</p>
      <ul className="pack-features-future">{pack.features.map((f, i) => <li key={i}>{f}</li>)}</ul>
      <div className="pack-price-future">
        <span className="price-big">€{pack.price}</span>
        <span className="price-per">(€{pack.pricePerCan}/lata)</span>
      </div>
      <button className="buy-button-future" style={{ background: pack.color }} onClick={() => onBuy(pack)} disabled={isProcessing}>
        {isProcessing ? <><FaSpinner className="spin" /> PROCESANDO</> : <><FaShoppingCart /> COMPRAR AHORA</>}
      </button>
      {!user && <p className="login-warning-future"><FaShieldAlt /> Inicia sesión para comprar</p>}
    </div>
  );
};

export default Home;